import { useCallback, useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, FileText, Mail, RefreshCw, ChevronLeft, ChevronRight, History } from "lucide-react";
import { toast } from "sonner";
import OutlookReportDialog from "./OutlookReportDialog";

const STATUS_STYLE = {
  running: "bg-sky-50 text-sky-700 border-sky-200",
  queued: "bg-amber-50 text-amber-800 border-amber-200",
  paused: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-zinc-100 text-zinc-600 border-zinc-200",
  failed: "bg-red-50 text-red-700 border-red-200",
};
const STATUSES = ["running", "queued", "paused", "completed", "cancelled", "failed"];
const fmtT = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function ContentDialog({ batch, open, onOpenChange }) {
  const [content, setContent] = useState(null);
  useEffect(() => {
    if (!open || !batch?.id) return;
    setContent(null);
    api.get(`/marketing/outlook/batches/${batch.id}/content`)
      .then((r) => setContent(r.data))
      .catch((e) => toast.error(fmtError(e)));
  }, [open, batch?.id]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="history-content-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">Email content</DialogTitle>
          <DialogDescription className="text-xs">
            Subject: <strong>{content?.email_subject || content?.subject || "—"}</strong> · Sender: {content?.sender || "—"}
          </DialogDescription>
        </DialogHeader>
        {!content ? (
          <Skeleton className="h-48 rounded-2xl" />
        ) : (
          <div
            className="rounded-2xl border border-border/60 bg-white p-4 text-sm overflow-x-auto"
            data-testid="history-content-body"
            dangerouslySetInnerHTML={{ __html: content.html || "<i>No content stored</i>" }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MarketingHistoryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [recipient, setRecipient] = useState("");
  const [status, setStatus] = useState("");
  const [startedBy, setStartedBy] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [users, setUsers] = useState([]);
  const [reportBatch, setReportBatch] = useState(null);
  const [contentBatch, setContentBatch] = useState(null);
  const [retrying, setRetrying] = useState("");

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/outlook/batches/history", {
        params: {
          q: q || undefined, recipient: recipient || undefined, status: status || undefined,
          started_by: startedBy || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined,
          page: p, page_size: 20,
        },
      });
      setData(data);
    } catch (e) { toast.error(fmtError(e)); } finally { setLoading(false); }
  }, [q, recipient, status, startedBy, dateFrom, dateTo, page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, recipient, status, startedBy, dateFrom, dateTo]);

  const goPage = (p) => { setPage(p); load(p); };

  const retryFailed = async (b) => {
    setRetrying(b.id);
    try {
      const { data } = await api.post(`/marketing/outlook/batches/${b.id}/retry-failed`);
      toast.success(`Retrying ${data.retrying ?? "failed"} email(s)`);
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setRetrying(""); }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-4" data-testid="marketing-history-panel">
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground flex items-center gap-1.5">
            <History className="h-3 w-3" /> Marketing History — permanent archive of every campaign
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9 rounded-2xl h-10" placeholder="Search campaign / subject / sender…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="history-search-input" />
            </div>
            <Input className="rounded-2xl h-10" placeholder="Recipient email contains…" value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="history-recipient-input" />
            <Select value={status || "__all"} onValueChange={(v) => setStatus(v === "__all" ? "" : v)}>
              <SelectTrigger className="rounded-2xl h-10" data-testid="history-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
            {users.length > 0 && (
              <Select value={startedBy || "__all"} onValueChange={(v) => setStartedBy(v === "__all" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-10" data-testid="history-user-filter"><SelectValue placeholder="Sent by anyone" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__all">Sent by anyone</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Input type="date" className="rounded-2xl h-10" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="history-date-from" />
            <Input type="date" className="rounded-2xl h-10" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="history-date-to" />
          </div>
        </CardContent>
      </Card>

      {loading && !data ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-3xl" />)}</div>
      ) : !data || data.rows.length === 0 ? (
        <Card className="rounded-3xl border-border/60"><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="history-empty">
          No campaigns match. Every campaign ever sent stays archived here permanently.
        </CardContent></Card>
      ) : (
        <>
          <p className="text-xs text-muted-foreground" data-testid="history-count">{data.total} campaign{data.total === 1 ? "" : "s"} in history</p>
          {data.rows.map((b) => {
            const pending = Math.max(0, (b.total || 0) - (b.sent || 0) - (b.failed || 0));
            return (
              <Card key={b.id} className="rounded-3xl border-border/60 shadow-soft" data-testid={`history-row-${b.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{b.email_subject || b.subject}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Created {fmtT(b.started_at)} · Sent by <strong>{b.started_by_name || "—"}</strong> · Sender {b.sender || "—"}
                        {b.finished_at ? ` · Finished ${fmtT(b.finished_at)}` : ""}
                      </p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_STYLE[b.status] || "bg-muted"}`} data-testid={`history-status-${b.id}`}>
                      {b.status}
                    </Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-4 gap-2 max-w-md">
                    {[["Total", b.total, "text-foreground"], ["Sent", b.sent, "text-emerald-700"], ["Failed", b.failed, "text-red-600"], ["Pending", pending, "text-amber-600"]].map(([l, v, c]) => (
                      <div key={l} className="rounded-2xl bg-muted/40 border border-border/60 px-2 py-1.5 text-center">
                        <p className={`font-display text-base font-extrabold ${c}`}>{(v || 0).toLocaleString()}</p>
                        <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">{l}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs tap" onClick={() => setReportBatch(b)} data-testid={`history-view-report-${b.id}`}>
                      <FileText className="h-3.5 w-3.5 mr-1" /> View report
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs tap" onClick={() => setContentBatch(b)} data-testid={`history-view-content-${b.id}`}>
                      <Mail className="h-3.5 w-3.5 mr-1" /> View content
                    </Button>
                    {(b.failed || 0) > 0 && (
                      <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs tap text-red-600 border-red-200 hover:bg-red-50" disabled={retrying === b.id} onClick={() => retryFailed(b)} data-testid={`history-retry-${b.id}`}>
                        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${retrying === b.id ? "animate-spin" : ""}`} /> Retry failed
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-1" data-testid="history-pagination">
              <Button variant="outline" size="sm" className="rounded-full h-9 w-9 p-0" disabled={page <= 1} onClick={() => goPage(page - 1)} data-testid="history-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" className="rounded-full h-9 w-9 p-0" disabled={page >= totalPages} onClick={() => goPage(page + 1)} data-testid="history-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      <OutlookReportDialog batch={reportBatch} open={!!reportBatch} onOpenChange={(v) => { if (!v) setReportBatch(null); }} />
      <ContentDialog batch={contentBatch} open={!!contentBatch} onOpenChange={(v) => { if (!v) setContentBatch(null); }} />
    </div>
  );
}
